import { LoginOrigin, type User } from '@/models/User';

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useLDClient } from 'launchdarkly-vue-client-sdk';

import { initializeAnalytics } from '@/analytics';

import { handleUnauthorizedRequest } from '@/utils/fetch';

import { useSessionStore } from './useSessionStore';

interface GoodHabitzLDContext {
  kind: 'multi';
  company: {
    key: string;
    name: string;
    country: string;
  };
  language: {
    key: string;
  };
  loginOrigin: {
    key: string;
  };
  // This will be conditionally added when the company is a GoodHabitz Entity
  // So we reduce the number of LD MAU and still able to do segmentation within GoodHabitz :)
  goodhabitzEmployee?: {
    key: string;
    firstName: string;
    lastName: string;
  };
}

export const useUserStore = defineStore('user', () => {
  const { locale } = useI18n();

  const user = ref<User>();

  const { identify: setProfileOnLaunchDarkly } = useLDClient();

  const sessionStore = useSessionStore();

  const canDownloadCertificates = computed(
    () =>
      sessionStore.platform !== LoginOrigin.RS &&
      user.value &&
      Boolean(user.value.FirstName) &&
      Boolean(user.value.LastName) &&
      !['undisclosed', '-'].includes(user.value.FirstName),
  );

  const setUser = (newUser: User) => {
    if (newUser.Id <= 0) {
      console.error('Anonymous web session detected.');
      handleUnauthorizedRequest();
      return;
    }

    if (user.value) return;

    const ldContext: GoodHabitzLDContext = {
      kind: 'multi',
      company: {
        key: newUser.ExternalCompanyReference,
        name: newUser.CompanyName,
        country: newUser.Country,
      },
      language: {
        key: locale.value.toLowerCase(),
      },
      loginOrigin: {
        key: newUser.LoginOrigin,
      },
    };

    const isGoodHabitzEntity = newUser.CompanyTypeId === 1;
    if (isGoodHabitzEntity) {
      ldContext.goodhabitzEmployee = {
        key: newUser.ExternalPersonReference,
        firstName: newUser.FirstName,
        lastName: newUser.LastName,
      };
    }

    // This project is using anonymous contexts until this one is called.
    // TODO: Refactor the project in a way that it awaits user initialization and then init the LD
    setProfileOnLaunchDarkly(ldContext);

    initializeAnalytics(newUser, locale.value.toLowerCase());

    user.value = newUser;
  };

  return {
    user,
    canDownloadCertificates,
    setUser,
  };
});
